import React from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const formatCompletionDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

const TechnicalView = ({ customer, technicalWork }) => {
  const getStatus = () => {
    if (customer?.status === "technical_complete") {
      return { label: "Complete", color: "bg-success/10 text-success" };
    }
    if (customer?.status === "technical_inprogress") {
      return { label: "In Progress", color: "bg-warning/10 text-warning" };
    }
    if (customer?.status === "technical_pending") {
      return {
        label: "Pending",
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      };
    }
    return { label: "Not Started", color: "bg-muted text-muted-foreground" };
  };

  const status = getStatus();
  const panelsData = technicalWork?.panels || {};
  const wiringInverter = technicalWork?.wiring_inverter || {};
  const wiring = technicalWork?.wiring || {};
  const earthing = technicalWork?.earthing || {};
  const installationPhotos = technicalWork?.installation_photos || {};
  const installationCompletedAt = technicalWork?.installation_completed_at
    ? formatCompletionDateTime(technicalWork.installation_completed_at)
    : null;

  const PANEL_SLOTS = [1, 2, 3, 4];
  const feetToMeters = (feetValue) => {
    const feet = Number.parseFloat(feetValue);
    if (!Number.isFinite(feet)) return null;
    return (feet * 0.3048).toFixed(2);
  };
  const formatCableLength = (feetValue, metersValue) => {
    const feet = String(feetValue ?? "").trim();
    const meters =
      String(metersValue ?? "").trim() ||
      feetToMeters(feetValue) ||
      "";
    if (!feet && !meters) return "—";
    if (!feet) return `${meters} m`;
    if (!meters) return `${feet} ft`;
    return `${feet} ft (${meters} m)`;
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Wrench" size={20} className="text-primary" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Technical Phase
        </h2>
        <span
          className={`ml-auto inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
        >
          {status.label}
        </span>
      </div>

      {installationCompletedAt && (
        <div className="mb-6 p-4 rounded-lg border border-border bg-muted/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Installation completion
          </p>
          <p className="text-sm font-medium text-foreground">
            {installationCompletedAt}
          </p>
        </div>
      )}

      <div className="space-y-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon
              name="Info"
              size={20}
              className="text-primary flex-shrink-0 mt-0.5"
            />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Panel serial numbers
              </h4>
              <p className="text-xs text-muted-foreground">
                Four panels: nameplate photos are captured in the workflow.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {PANEL_SLOTS.map((panelId) => {
            const panel = panelsData[panelId] || {};
            const panelSerialPhoto =
              panel.serialNumberPhoto || panel.photo;
            const hasPhoto = Boolean(panelSerialPhoto);
            return (
              <div
                key={panelId}
                className="p-4 rounded-lg border border-border bg-muted/30"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                    Panel {panelId}
                    {hasPhoto && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                        <Icon name="Check" size={12} />
                        Complete
                      </span>
                    )}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                      Serial number photo
                    </label>
                    {panelSerialPhoto ? (
                      <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                        <Image
                          src={panelSerialPhoto}
                          alt={`Panel ${panelId} serial number`}
                          className="w-full h-full object-contain bg-muted"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border">
                        <div className="text-center">
                          <Icon
                            name="Image"
                            size={32}
                            className="mx-auto mb-2 text-muted-foreground"
                          />
                          <p className="text-sm text-muted-foreground">
                            No photo uploaded
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Inverter */}
        <div className="p-4 rounded-lg border border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">
            Inverter
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Inverter make
              </label>
              <p className="text-sm text-foreground font-medium">
                {wiringInverter.inverterMake || wiringInverter.inverter_make || "—"}
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Inverter serial number photo
              </label>
              {wiringInverter.inverterSerialPhoto || wiringInverter.inverter_serial_photo ? (
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted max-w-md">
                  <Image
                    src={
                      wiringInverter.inverterSerialPhoto ||
                      wiringInverter.inverter_serial_photo
                    }
                    alt="Inverter serial number nameplate"
                    className="w-full h-full object-contain bg-muted"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border max-w-md">
                  <Icon
                    name="Image"
                    size={32}
                    className="text-muted-foreground"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Installation photos */}
        <div className="p-4 rounded-lg border border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">
            Installation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Panel & Structure Side View (with customer)
              </label>
              {installationPhotos.panelSideWithCustomer ? (
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={installationPhotos.panelSideWithCustomer}
                    alt="Panel and structure side view with customer"
                    className="w-full h-full object-contain bg-muted"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border">
                  <Icon
                    name="Image"
                    size={32}
                    className="text-muted-foreground"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Panel & Structure Front View (with customer)
              </label>
              {installationPhotos.panelFrontWithCustomer ? (
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={installationPhotos.panelFrontWithCustomer}
                    alt="Panel and structure front view with customer"
                    className="w-full h-full object-contain bg-muted"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border">
                  <Icon
                    name="Image"
                    size={32}
                    className="text-muted-foreground"
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Inverter Photo
              </label>
              {installationPhotos.inverterInstallationPhoto ? (
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted max-w-md">
                  <Image
                    src={installationPhotos.inverterInstallationPhoto}
                    alt="Installed inverter"
                    className="w-full h-full object-contain bg-muted"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border max-w-md">
                  <Icon
                    name="Image"
                    size={32}
                    className="text-muted-foreground"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Wiring Section */}
        <div className="p-4 rounded-lg border border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">
            Wiring
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                DC Cable Length (4 sqmm) - Red
              </label>
              <p className="text-sm text-foreground font-medium">
                {formatCableLength(wiring.dcRedFeet, null)}
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                DC Cable Length (4 sqmm) - Black
              </label>
              <p className="text-sm text-foreground font-medium">
                {formatCableLength(wiring.dcBlackFeet, null)}
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                DC Cable Length (4 sqmm) - Total
              </label>
              <p className="text-sm text-foreground font-medium">
                {String(wiring.dcMeters || "").trim()
                  ? `${wiring.dcMeters} m`
                  : "—"}
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                AC Cable Length (2 Core, 4 sqmm)
              </label>
              <p className="text-sm text-foreground font-medium">
                {formatCableLength(wiring.acFeet, wiring.acMeters)}
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Earthing Cable Length (16 sqmm)
              </label>
              <p className="text-sm text-foreground font-medium">
                {formatCableLength(wiring.earthingFeet, wiring.earthingMeters)}
              </p>
            </div>
          </div>
        </div>

        {/* Earthing Section */}
        <div className="p-4 rounded-lg border border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">
            Earthing
          </h3>
          <div className="max-w-md">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Earthing photo
            </label>
            {earthing.photo1 || earthing.photo2 ? (
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                <Image
                  src={earthing.photo1 || earthing.photo2}
                  alt="Earthing installation"
                  className="w-full h-full object-contain bg-muted"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border">
                <Icon
                  name="Image"
                  size={32}
                  className="text-muted-foreground"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalView;
